/**
 * The area-level path: preview and run, composed from engine
 * primitives (OPEN_QUESTIONS Q31).
 *
 * runAnalysis is microdata-only, so an area-level file's single cell
 * is run here by composing the same stages runAnalysis composes for
 * microdata — transform (9.5), covariate selection (9.14), the
 * minimum-conditions gate (9.15 as shipped under decision S4 and
 * directive C-7: fit at five or more areas, warn below ten, refuse
 * below five), the Fay-Herriot fit
 * (9.6-9.9), back-transformation (9.10), Full-mode bootstrap (9.12),
 * benchmarking (9.13, off unless enabled), and disclosure with
 * complementary suppression (Section 10) — into the same
 * AnalysisResult shape, so every downstream consumer (results table,
 * chart, diagnostics, exports) is path-independent.
 *
 * Policy readings, mirroring runAnalysis (decision S4):
 * - areas whose estimate or variance is missing are excluded from
 *   the model and named in the issues (the io conversion already
 *   names them; a summary MISSING_DIRECT_ESTIMATE issue is added);
 * - out-of-sample (model-only) estimates stay off (decision O3);
 * - the transform's design effect is 1: the published sample size is
 *   treated as the effective sample size, the convention the io demo
 *   test pins. On the arcsine path psi comes from that effective
 *   size (9.5); the published variance drives the direct interval
 *   shown beside the model estimate, and is psi itself on the
 *   linear path.
 */

import type { FeasibilityPreview, CellPreview, DomainPreview } from '@sae/automation';
import type {
  AnalysisResult,
  AreaLevelCellInput,
  AreaLevelDomainInput,
  BackTransformedEstimate,
  BenchmarkingStatus,
  CellResult,
  CovariateDrop,
  CovariateSelectionStep,
  DisclosureConfig,
  DisclosureRuleInput,
  DomainKey,
  DomainResult,
  FayHerriotDomainInput,
  Issue,
  ProgressEvent,
} from '@sae/engine';
import {
  applyBenchmarking,
  applyComplementarySuppression,
  applyDisclosureRules,
  assessValidatedRange,
  assembleBootstrapEstimates,
  backTransformAreas,
  DEFAULT_FAY_HERRIOT_FIT_CONFIG,
  fitFayHerriot,
  InsufficientAreasError,
  kornGraubardBounds,
  normalQuantile,
  outOfValidatedRangeIssue,
  parametricBootstrapMse,
  resolveValidatedRangeConfig,
  selectCovariates,
  transformAreaLevelDomains,
} from '@sae/engine';
import type { IoIssue } from '@sae/io';

import { resolveTransform } from '../domain/defaults.js';
import type { CovariateMode, RunConfigState } from '../domain/types.js';

/**
 * The Section 9.15 fit gate as shipped (owner decision S4, directive
 * C-7): a cell is fitted when at least five areas have direct
 * estimates; below five it is refused. This equals the fit's own
 * absolute floor (engine MIN_AREAS_TO_FIT).
 */
export const MIN_AREAS = 5;
/** The fit's absolute floor (engine MIN_AREAS_TO_FIT). */
export const MIN_AREAS_FLOOR = 5;
/** Fitted cells below this many areas carry the LOW_AREA_COUNT warning (S4). */
export const LOW_AREA_COUNT_BELOW = 10;

/**
 * The direct interval this path reports for an area-level domain:
 * the Wald interval on the published sampling variance, clamped to
 * [0, 1] (the construction the module header records — published
 * area-level data carries no design from which the brief 9.3
 * logit / Korn-Graubard forms could be built, unless the file
 * publishes degrees of freedom for the standard's width tests).
 * ONE implementation for the whole path (Tier 2 Item 7): the
 * preview, the disclosure inputs, the reliability measures, and
 * the reported domain all read this interval, so a direct domain's
 * interval width everywhere is the width of the interval actually
 * reported — the engine pipeline's convention (runAnalysis reads
 * its widths from the reported interval). The path previously
 * formed the unclamped width 2 * z * se inline at three sites,
 * which disagrees with the reported interval's width wherever an
 * end clamps.
 */
function directIntervalOf(
  d: AreaLevelDomainInput,
  z: number,
): { lower: number; upper: number } {
  const se = Math.sqrt(Math.max(0, d.samplingVariance));
  return {
    lower: Math.max(0, d.directEstimate - z * se),
    upper: Math.min(1, d.directEstimate + z * se),
  };
}

/** The engine's relative-width convention (runAnalysis). */
function relativeWidth(width: number, base: number): number {
  return base > 0 ? width / base : width > 0 ? Infinity : 0;
}

/**
 * The domain's event count for the disclosure rules (Tier 2
 * Item 6): the file's published yes count when it carries one;
 * otherwise the count the published proportion itself determines
 * at the boundaries — zero events when the proportion is 0, all
 * events when it is 1 (weights are positive, so a published
 * proportion at a boundary fixes the count); otherwise unknown,
 * and the standard's zero-event review flag does not fire.
 */
function eventCountOf(d: AreaLevelDomainInput): number | undefined {
  if (d.yesCount !== undefined) return d.yesCount;
  if (d.directEstimate === 0) return 0;
  if (d.directEstimate === 1) return d.sampleSize;
  return undefined;
}

/**
 * The proportion standard's width-test fields for a direct domain
 * (Tier 2 Item 6): the width of the domain's Korn-Graubard
 * interval (brief 9.3), the interval the standard defines its
 * width tests on for complex surveys. The construction needs the
 * design degrees of freedom; when the file does not publish them
 * the fields are absent and the width tests fall back to the
 * reported interval's width — the same absence convention as the
 * degrees-of-freedom review flag.
 */
function standardWidthFieldsOf(
  d: AreaLevelDomainInput,
  confidence: number,
): Pick<
  DisclosureRuleInput,
  'standardIntervalWidth' | 'standardRelativeIntervalWidth'
> {
  if (d.degreesOfFreedom === undefined) return {};
  const kg = kornGraubardBounds(
    d.directEstimate,
    d.samplingVariance,
    d.sampleSize,
    d.degreesOfFreedom,
    1 - confidence,
  );
  const w = kg.upper - kg.lower;
  return {
    standardIntervalWidth: w,
    standardRelativeIntervalWidth:
      d.directEstimate > 0 ? w / d.directEstimate : w > 0 ? Infinity : 0,
  };
}

function ioIssueToIssue(issue: IoIssue): Issue {
  return {
    code: issue.code,
    severity: issue.severity === 'error' ? 'blocking' : 'warning',
    message: issue.message,
    ...(issue.areaId !== undefined
      ? {
          domain: {
            cellId: '',
            indicatorId: '',
            areaId: issue.areaId,
          },
        }
      : {}),
  };
}

/**
 * Feasibility preview for an area-level cell, in the same
 * FeasibilityPreview shape the microdata preview returns, so Step 2
 * renders one summary for both paths. Projected statuses come from
 * the direct estimates through the engine's disclosure rules,
 * labeled projected (brief AUT-04: the model is usually more
 * precise than this direct-based projection).
 */
export function previewAreaLevel(
  cell: AreaLevelCellInput,
  disclosure: DisclosureConfig,
  confidence = 0.95,
): FeasibilityPreview {
  const z = normalQuantile(0.5 + confidence / 2);
  const domainAreas = new Set(cell.domains.map((d) => d.areaId));
  const candidateAreas = [
    ...new Set([
      ...cell.domains.map((d) => d.areaId),
      ...cell.covariates.map((r) => r.areaId),
    ]),
  ].sort();
  const missingDirectAreas = candidateAreas.filter((a) => !domainAreas.has(a));

  const inputs: DisclosureRuleInput[] = cell.domains.map((d) => {
    const se = Math.sqrt(Math.max(0, d.samplingVariance));
    const directInterval = directIntervalOf(d, z);
    const width = directInterval.upper - directInterval.lower;
    const key: DomainKey = {
      cellId: cell.indicatorId,
      indicatorId: cell.indicatorId,
      areaId: d.areaId,
    };
    return {
      domain: key,
      n: d.sampleSize,
      cv: d.directEstimate > 0 ? se / d.directEstimate : se > 0 ? Infinity : 0,
      intervalWidth: width,
      relativeIntervalWidth: relativeWidth(width, d.directEstimate),
      psuCount: 0,
      effectiveSampleSize: d.sampleSize,
      ...(d.degreesOfFreedom !== undefined
        ? { degreesOfFreedom: d.degreesOfFreedom }
        : {}),
      ...(eventCountOf(d) !== undefined
        ? { eventCount: eventCountOf(d) }
        : {}),
      // Every preview domain is a direct estimate, so the
      // standard's Korn-Graubard width fields apply throughout.
      ...standardWidthFieldsOf(d, confidence),
    };
  });
  const outcomes = applyComplementarySuppression(
    applyDisclosureRules(inputs, disclosure),
    inputs,
  );
  const outcomeByArea = new Map(outcomes.map((o) => [o.domain.areaId, o]));

  const domains: DomainPreview[] = candidateAreas.map((areaId) => {
    const d = cell.domains.find((x) => x.areaId === areaId);
    if (d === undefined) {
      return {
        areaId,
        n: 0,
        effectiveSampleSize: 0,
        projectedCv: null,
        projectedStatus: 'no-direct-estimate' as const,
      };
    }
    const se = Math.sqrt(Math.max(0, d.samplingVariance));
    const outcome = outcomeByArea.get(areaId);
    return {
      areaId,
      n: d.sampleSize,
      effectiveSampleSize: d.sampleSize,
      projectedCv: d.directEstimate > 0 ? se / d.directEstimate : null,
      projectedStatus: (outcome?.status ?? 'publish') as DomainPreview['projectedStatus'],
      ...(outcome?.ruleId !== undefined
        ? { projectedRuleId: outcome.ruleId }
        : {}),
    };
  });

  const fittable = cell.domains.length;
  // S4 / directive C-7: below five areas the cell is refused; five to
  // nine areas fit with the LOW_AREA_COUNT warning; missing direct
  // estimates also surface as fitted-with-warning.
  const outcome: CellPreview['outcome'] =
    fittable < MIN_AREAS
      ? 'refused'
      : fittable < LOW_AREA_COUNT_BELOW || missingDirectAreas.length > 0
        ? 'fitted-with-warning'
        : 'fitted';
  const cellPreview: CellPreview = {
    cellId: cell.indicatorId,
    indicator: cell.indicatorId,
    presentAreas: fittable,
    fittableAreas: fittable,
    missingDirectAreas,
    outcome,
    projectedPublish: domains.filter((d) => d.projectedStatus === 'publish').length,
    projectedFlagged: domains.filter((d) => d.projectedStatus === 'flagged').length,
    projectedSuppressed: domains.filter((d) => d.projectedStatus === 'suppressed').length,
    domains,
  };
  return {
    ok: true,
    log: [],
    totalCells: 1,
    fittedCells: outcome === 'fitted' || outcome === 'fitted-with-warning' ? 1 : 0,
    warningCells: outcome === 'fitted-with-warning' ? 1 : 0,
    // The area-level preview's outcome has three tiers (S4): the
    // 'insufficient-areas' tier exists only in the microdata
    // feasibility preview, where a configured minAreas above the
    // five-area floor can bind.
    insufficientCells: 0,
    refusedCells: outcome === 'refused' ? 1 : 0,
    candidateAreas,
    areasWithoutDirect: missingDirectAreas,
    projectedSuppressedDomains: cellPreview.projectedSuppressed,
    cells: [cellPreview],
  };
}

export interface AreaLevelRunArgs {
  readonly cell: AreaLevelCellInput;
  readonly ioIssues: readonly IoIssue[];
  readonly covariateModes: Readonly<Record<string, CovariateMode>>;
  readonly disclosure: DisclosureConfig;
  readonly runConfig: RunConfigState;
  readonly onProgress?: (event: ProgressEvent) => void;
}

/** Run the area-level cell end to end (see the module header). */
export function runAreaLevel(args: AreaLevelRunArgs): AnalysisResult {
  const { cell, runConfig } = args;
  const startedAt = new Date().toISOString();
  const transform = resolveTransform(runConfig.transform);
  const confidence = runConfig.confidence;
  const z = normalQuantile(0.5 + confidence / 2);
  const emit = (stage: string, completed: number, total: number, message?: string) =>
    args.onProgress?.({
      stage,
      completed,
      total,
      cellId: cell.indicatorId,
      ...(message !== undefined ? { message } : {}),
    });

  const issues: Issue[] = args.ioIssues.map(ioIssueToIssue);
  const cellIssues: Issue[] = [];

  const domainByArea = new Map(cell.domains.map((d) => [d.areaId, d]));
  const excludedAreas = cell.covariates
    .map((r) => r.areaId)
    .filter((a) => !domainByArea.has(a));
  if (excludedAreas.length > 0) {
    cellIssues.push({
      code: 'MISSING_DIRECT_ESTIMATE',
      severity: 'warning',
      cellId: cell.indicatorId,
      message:
        `No direct estimate is available for ${excludedAreas.length} ` +
        `area${excludedAreas.length === 1 ? '' : 's'} (${excludedAreas.join(', ')}); ` +
        'they are excluded from the model and listed here (decision S4).',
    });
  }

  // ---- Covariate candidates after the user's exclusions. ----------
  const allCovariates =
    cell.covariates.length > 0 ? Object.keys(cell.covariates[0].values) : [];
  const modeOf = (name: string): CovariateMode =>
    args.covariateModes[name] ?? 'auto';
  const candidates = allCovariates.filter((n) => modeOf(n) !== 'excluded');
  const locked = candidates.filter((n) => modeOf(n) === 'locked');
  const restrictCell = (names: readonly string[]): AreaLevelCellInput => ({
    indicatorId: cell.indicatorId,
    domains: cell.domains,
    covariates: cell.covariates.map((row) => ({
      areaId: row.areaId,
      values: Object.fromEntries(
        names
          .filter((n) => row.values[n] !== undefined)
          .map((n) => [n, row.values[n]]),
      ),
      ...(row.populationCount !== undefined
        ? { populationCount: row.populationCount }
        : {}),
    })),
  });

  emit('prepare', 0, 4, 'Preparing the area-level model');
  const inSampleCount = cell.domains.length;
  const gatePasses = inSampleCount >= MIN_AREAS;

  let fit: CellResult['fit'];
  let estimates: BackTransformedEstimate[] = [];
  let selectedCovariates: readonly string[] = [];
  // Disclosure record state (Tier 3 verdict fix 1): the drops the
  // selection stage accounts for, assembled into the cell's
  // selection record when the cell result is built.
  let selectionDrops: CovariateDrop[] | undefined;
  let mseMethod: 'analytical' | 'bootstrap' = 'analytical';
  let benchmarkApplied = false;
  let fitted = false;
  let statewideFigures: CellResult['statewideFigures'];

  if (gatePasses) {
    // ---- Transform with every candidate, for selection (9.14). ----
    const meanBiasCorrection = runConfig.transformBiasCorrection === true;
    let domains = transformAreaLevelDomains(restrictCell(candidates), {
      transform,
      designEffect: 1,
      meanBiasCorrection,
    }).filter((d) => !d.outOfSample);
    selectedCovariates = [...locked];
    const autoCandidates = candidates.filter((n) => modeOf(n) === 'auto');
    let selectionPath: readonly CovariateSelectionStep[] | undefined;
    let selectionFailed = false;
    if (autoCandidates.length > 0 && domains.length > 0) {
      try {
        const byArea = new Map(
          restrictCell(candidates).covariates.map((r) => [r.areaId, r.values]),
        );
        const selection = selectCovariates({
          thetaHat: domains.map((d) => d.thetaHat),
          psi: domains.map((d) => d.psi),
          candidates: Object.fromEntries(
            candidates.map((name) => [
              name,
              domains.map((d) => byArea.get(d.areaId)?.[name] ?? NaN),
            ]),
          ),
          config: {
            criterion: 'bic',
            scope: 'overall',
            correlationThreshold: 0.9,
            vifThreshold: 10,
            locked,
            excluded: [],
          },
        });
        selectionPath = selection.path;
        selectedCovariates = candidates.filter((n) =>
          selection.selected.includes(n),
        );
      } catch {
        selectionFailed = true;
        cellIssues.push({
          code: 'SELECTION_SKIPPED',
          severity: 'warning',
          cellId: cell.indicatorId,
          message:
            'Automatic covariate selection could not run for this cell; the locked covariates were used.',
        });
        selectedCovariates = [...locked];
      }
    }
    // Disclosure record (Tier 3 verdict fix 1): every offered
    // covariate not in the final model is accounted to one rule.
    {
      const drops: CovariateDrop[] = [];
      const screenReasonByName = new Map<string, CovariateDrop['rule']>();
      for (const step of selectionPath ?? []) {
        if (
          step.variable === undefined ||
          !Number.isNaN(step.criterionValue) ||
          step.reason === undefined
        ) {
          continue;
        }
        screenReasonByName.set(step.variable, step.reason);
      }
      for (const name of allCovariates) {
        if (selectedCovariates.includes(name)) continue;
        if (modeOf(name) === 'excluded') {
          drops.push({ name, rule: 'user-excluded' });
        } else if (selectionFailed) {
          drops.push({ name, rule: 'selection-unavailable' });
        } else {
          drops.push({
            name,
            rule: screenReasonByName.get(name) ?? 'not-selected',
          });
        }
      }
      selectionDrops = drops;
    }
    if (selectedCovariates.length !== candidates.length) {
      domains = transformAreaLevelDomains(restrictCell(selectedCovariates), {
        transform,
        designEffect: 1,
        meanBiasCorrection,
      }).filter((d) => !d.outOfSample);
    }

    // ---- Fit (9.6-9.9). -------------------------------------------
    emit('fit', 1, 4, 'Fitting the Fay-Herriot model');
    try {
      fit = fitFayHerriot(domains, DEFAULT_FAY_HERRIOT_FIT_CONFIG);
      fitted = true;
    } catch (err) {
      if (!(err instanceof InsufficientAreasError)) throw err;
      fit = undefined;
    }

    if (fitted && fit !== undefined) {
      estimates = backTransformAreas(fit, { method: 'bc', confidence, transform });

      // ---- Bootstrap MSEs (9.12), as runAnalysis does: Full mode,
      // and — directive C-1 — any cell fitted by the adjusted REML,
      // whose analytical MSE omits that estimator's bias term.
      if (runConfig.mode === 'full' || fit.methodUsed === 'adjusted-reml') {
        emit(
          'uncertainty',
          2,
          4,
          runConfig.mode === 'full'
            ? 'Bootstrap uncertainty (Full mode)'
            : 'Bootstrap uncertainty (adjusted-REML cell, directive C-1)',
        );
        const counts = populationCountsOf(cell);
        const countsComplete = domains.every(
          (d) => counts[d.areaId] !== undefined,
        );
        const benchmarkingOn = runConfig.benchmarkingEnabled;
        const boot = parametricBootstrapMse(domains as FayHerriotDomainInput[], {
          replicates: runConfig.bootstrapB,
          seed: `${runConfig.seed}:${cell.indicatorId}`,
          confidence,
          transform,
          meanBiasCorrection,
          fitConfig: DEFAULT_FAY_HERRIOT_FIT_CONFIG,
          benchmarking:
            benchmarkingOn && countsComplete
              ? { external: runConfig.benchmarkingMethod, internal: false }
              : { external: 'none', internal: false },
          ...(benchmarkingOn && countsComplete
            ? {
                benchmarkTarget: statewideDirect(cell, counts),
                populationCounts: counts,
              }
            : {}),
        });
        // The bootstrap interval assembly is the engine's single
        // construction (assembleBootstrapEstimates; Tier 2 Item 7):
        // intervals on the fitted scale from the bootstrap
        // mse_theta, mapped back, with rmse = sqrt(mse_p). This
        // module previously carried a private copy whose linear
        // branch formed the half-width from mse_p instead — the
        // copy is deleted, not mirrored (see
        // docs/TIER2_ITEM7_AUDIT.md).
        estimates = assembleBootstrapEstimates(estimates, fit, boot, {
          confidence,
          transform,
        });
        mseMethod = 'bootstrap';
      }

      // ---- Statewide figures for display (proportion scale). -----
      // The population-weighted mean of the fitted model estimates
      // against the population-weighted statewide direct, computed
      // here, before benchmarking, so the pair describes the fitted
      // model (the same construction runAnalysis stores on its cell
      // results). Falls back to unweighted means of the estimates and
      // directs when a fitted area has no population count. The fit
      // diagnostics' aggregation means live on the transformed scale
      // and are never display values.
      {
        const counts = populationCountsOf(cell);
        const inSample = new Set(
          fit.areas.filter((a) => !a.outOfSample).map((a) => a.areaId),
        );
        const fittedEstimates = estimates.filter((e) =>
          inSample.has(e.areaId),
        );
        let weightSum = 0;
        let weightedModel = 0;
        let sumModel = 0;
        let sumDirect = 0;
        let nDirect = 0;
        let complete = fittedEstimates.length > 0;
        for (const e of fittedEstimates) {
          const c = counts[e.areaId];
          const d = domainByArea.get(e.areaId);
          if (c === undefined || d === undefined) complete = false;
          sumModel += e.estimate;
          if (d !== undefined) {
            sumDirect += d.directEstimate;
            nDirect += 1;
          }
          if (c !== undefined) {
            weightedModel += c * e.estimate;
            weightSum += c;
          }
        }
        if (fittedEstimates.length > 0 && nDirect > 0) {
          statewideFigures =
            complete && weightSum > 0
              ? {
                  modelMean: weightedModel / weightSum,
                  directEstimate: statewideDirect(cell, counts),
                  populationWeighted: true,
                }
              : {
                  modelMean: sumModel / fittedEstimates.length,
                  directEstimate: sumDirect / nDirect,
                  populationWeighted: false,
                };
        }
      }

      // ---- Benchmarking (9.13); on by default (D-4). --------------
      if (runConfig.benchmarkingEnabled) {
        const counts = populationCountsOf(cell);
        const complete = domains.every(
          (d) => counts[d.areaId] !== undefined,
        );
        if (!complete) {
          cellIssues.push({
            code: 'BENCHMARK_SKIPPED',
            severity: 'warning',
            cellId: cell.indicatorId,
            message:
              'Benchmarking was not applied because population counts are missing for one or more areas (brief 9.13).',
          });
        } else {
          try {
            estimates = applyBenchmarking(estimates, {
              method: runConfig.benchmarkingMethod,
              target: statewideDirect(cell, counts),
              populationCounts: counts,
              confidence,
              transform,
            });
            benchmarkApplied = true;
          } catch (err) {
            cellIssues.push({
              code: 'BENCHMARK_REJECTED',
              severity: 'warning',
              cellId: cell.indicatorId,
              message:
                'Benchmarking was not applied: ' +
                (err instanceof Error ? err.message : String(err)),
            });
          }
        }
      }
    }
  }

  if (!fitted) {
    cellIssues.push({
      code: 'INSUFFICIENT_AREAS',
      severity: 'warning',
      cellId: cell.indicatorId,
      message:
        `Only ${inSampleCount} areas have direct estimates; the model needs at least five areas to fit (brief 9.15, decision S4). Direct estimates are reported.`,
    });
  } else if (inSampleCount < LOW_AREA_COUNT_BELOW) {
    cellIssues.push({
      code: 'LOW_AREA_COUNT',
      severity: 'warning',
      cellId: cell.indicatorId,
      message: `The model was fitted on ${inSampleCount} areas, fewer than ten; treat the estimates with care (decision S4).`,
    });
  }

  // ---- Disclosure over every reported domain (Section 10, S6). ---
  emit('disclosure', 3, 4, 'Applying the disclosure rules');
  const estimateByArea = new Map(estimates.map((e) => [e.areaId, e]));
  const fitByArea = new Map((fit?.areas ?? []).map((a) => [a.areaId, a]));
  const disclosureInputs: DisclosureRuleInput[] = cell.domains.map((d) => {
    const model = estimateByArea.get(d.areaId);
    const se = Math.sqrt(Math.max(0, d.samplingVariance));
    const directInterval = directIntervalOf(d, z);
    const width =
      model !== undefined
        ? model.upper - model.lower
        : directInterval.upper - directInterval.lower;
    const base = model !== undefined ? model.estimate : d.directEstimate;
    return {
      domain: {
        cellId: cell.indicatorId,
        indicatorId: cell.indicatorId,
        areaId: d.areaId,
      },
      n: d.sampleSize,
      cv:
        model !== undefined
          ? model.cv
          : d.directEstimate > 0
            ? se / d.directEstimate
            : se > 0
              ? Infinity
              : 0,
      intervalWidth: width,
      relativeIntervalWidth: relativeWidth(width, base),
      psuCount: 0,
      effectiveSampleSize: d.sampleSize,
      ...(d.degreesOfFreedom !== undefined
        ? { degreesOfFreedom: d.degreesOfFreedom }
        : {}),
      ...(eventCountOf(d) !== undefined
        ? { eventCount: eventCountOf(d) }
        : {}),
      // The standard's Korn-Graubard width tests apply to domains
      // reported from their direct estimate; a model-reported
      // domain's width tests stay on its model interval.
      ...(model === undefined ? standardWidthFieldsOf(d, confidence) : {}),
      // The 'sae-standard' preset's inputs (Path to A guide,
      // Part 4.2): the model-based estimate, interval, and RMSE
      // exactly as back-transformation carried them.
      ...(model !== undefined
        ? {
            modelEstimate: model.estimate,
            modelLower: model.lower,
            modelUpper: model.upper,
            modelRmse: model.rmse,
          }
        : {}),
    };
  });
  const outcomes = applyComplementarySuppression(
    applyDisclosureRules(disclosureInputs, args.disclosure),
    disclosureInputs,
  );
  const outcomeByArea = new Map(outcomes.map((o) => [o.domain.areaId, o]));
  const counts = populationCountsOf(cell);

  const domainsOut: DomainResult[] = cell.domains.map((d) => {
    const se = Math.sqrt(Math.max(0, d.samplingVariance));
    const model = estimateByArea.get(d.areaId);
    const outcome = outcomeByArea.get(d.areaId);
    const reported = model !== undefined ? model.estimate : d.directEstimate;
    const directInterval = directIntervalOf(d, z);
    const width =
      model !== undefined
        ? model.upper - model.lower
        : directInterval.upper - directInterval.lower;
    const pop = counts[d.areaId];
    return {
      domain: {
        cellId: cell.indicatorId,
        indicatorId: cell.indicatorId,
        areaId: d.areaId,
      },
      direct: {
        domain: {
          cellId: cell.indicatorId,
          indicatorId: cell.indicatorId,
          areaId: d.areaId,
        },
        n: d.sampleSize,
        yesCount: d.yesCount ?? Math.round(d.directEstimate * d.sampleSize),
        weightedTotal: pop ?? 0,
        estimate: d.directEstimate,
        variance: d.samplingVariance,
        standardError: se,
        // Unknown for published area-level data unless the file
        // carries it; 0 marks "not published" (see Q31).
        degreesOfFreedom: d.degreesOfFreedom ?? 0,
        psuCount: 0,
        strataCount: 0,
        interval: {
          lower: directInterval.lower,
          upper: directInterval.upper,
          method: 'wald' as const,
        },
        flags: [],
      },
      ...(model !== undefined ? { model } : {}),
      estimateType: model !== undefined ? ('Model' as const) : ('Direct' as const),
      status: outcome?.status ?? 'publish',
      ...(outcome?.reason !== undefined
        ? { suppressionReason: outcome.reason }
        : {}),
      ...(outcome?.ruleId !== undefined
        ? { disclosureRuleId: outcome.ruleId }
        : {}),
      ...(outcome?.labels !== undefined
        ? { disclosureLabels: outcome.labels }
        : {}),
      reliability: {
        n: d.sampleSize,
        effectiveSampleSize: d.sampleSize,
        degreesOfFreedom: d.degreesOfFreedom ?? 0,
        ...(fitByArea.get(d.areaId) !== undefined
          ? { gamma: fitByArea.get(d.areaId)!.gamma }
          : {}),
        ...(model !== undefined
          ? { rmse: model.rmse, cv: model.cv }
          : {
              cv:
                d.directEstimate > 0
                  ? se / d.directEstimate
                  : se > 0
                    ? Infinity
                    : 0,
            }),
        intervalWidth: width,
        relativeIntervalWidth: relativeWidth(width, reported),
      },
      ...(pop !== undefined
        ? {
            complement: {
              estimate: 1 - reported,
              lower: 1 - (model !== undefined ? model.upper : directInterval.upper),
              upper: 1 - (model !== undefined ? model.lower : directInterval.lower),
              countYes: Math.round(d.directEstimate * d.sampleSize),
              countNo: d.sampleSize - Math.round(d.directEstimate * d.sampleSize),
              weightedYes: reported * pop,
              weightedNo: pop - reported * pop,
            },
          }
        : {}),
    };
  });

  // ---- Validated-range flag (directive RB-2, decision D-2). -----
  // Same engine helper as runAnalysis: the published sample sizes of
  // the fitted areas and the final model estimates. Informational.
  // The RB-4 triggers read the benchmarking outcome of the step
  // above: disabled in the run config is 'user-off', and any
  // pipeline-side non-application (BENCHMARK_SKIPPED,
  // BENCHMARK_REJECTED) is 'skipped'. The frame-counts status is
  // stated explicitly: this path carries no microdata design that
  // could supply frame PSU counts, so the Re-Review 4 no-counts
  // trigger (a microdata-path trigger) does not apply to it; since
  // Re-Review 5 the engine default for an unreported status is the
  // fail-safe 'not-supplied', so the inapplicability is stated
  // here rather than inherited.
  const benchmarkingStatus: BenchmarkingStatus = !runConfig.benchmarkingEnabled
    ? 'user-off'
    : benchmarkApplied
      ? 'applied'
      : 'skipped';
  const validatedRangeConfig = resolveValidatedRangeConfig();
  const validatedRange =
    fitted && fit !== undefined
      ? assessValidatedRange(
          fit.areas
            .filter((a) => !a.outOfSample)
            .map((a) => domainByArea.get(a.areaId)?.sampleSize ?? Number.NaN),
          estimates.map((e) => e.estimate),
          validatedRangeConfig,
          benchmarkingStatus,
          'supplied',
        )
      : undefined;
  if (validatedRange?.flagged) {
    cellIssues.push(
      outOfValidatedRangeIssue(cell.indicatorId, validatedRange, validatedRangeConfig),
    );
  }

  const cellResult: CellResult = {
    cellId: cell.indicatorId,
    indicatorId: cell.indicatorId,
    status: fitted ? 'fitted' : 'insufficient-areas',
    designEffect: 1,
    selectedCovariates,
    ...(selectionDrops !== undefined
      ? {
          selection: {
            offered: allCovariates,
            kept: selectedCovariates,
            dropped: selectionDrops,
            criterion: 'bic' as const,
          },
        }
      : {}),
    ...(fit !== undefined ? { fit } : {}),
    transform,
    mseMethod,
    benchmarkApplied,
    ...(benchmarkApplied
      ? { benchmarkingMethod: runConfig.benchmarkingMethod }
      : {}),
    ...(statewideFigures !== undefined ? { statewideFigures } : {}),
    ...(validatedRange !== undefined
      ? {
          outOfValidatedRange: validatedRange.flagged,
          validatedRange: {
            medianRespondentsPerArea: validatedRange.medianRespondentsPerArea,
            ...(validatedRange.smallestModelEstimate !== undefined
              ? { smallestModelEstimate: validatedRange.smallestModelEstimate }
              : {}),
            ...(validatedRange.largestModelEstimate !== undefined
              ? { largestModelEstimate: validatedRange.largestModelEstimate }
              : {}),
            fittedAreaCount: validatedRange.fittedAreaCount,
            benchmarkingStatus: validatedRange.benchmarkingStatus,
          },
        }
      : {}),
    issues: cellIssues,
  };

  emit('done', 4, 4, 'Run complete');
  return {
    domains: domainsOut,
    cells: [cellResult],
    issues: [...issues, ...cellIssues],
    manifest: {
      runId: `run-${startedAt}`,
      engineVersion: '0.0.0',
      startedAt,
      seed: runConfig.seed,
    },
  };
}

function populationCountsOf(
  cell: AreaLevelCellInput,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of cell.covariates) {
    if (row.populationCount !== undefined) counts[row.areaId] = row.populationCount;
  }
  return counts;
}

/** Population-weighted statewide direct estimate (the 9.13 target). */
function statewideDirect(
  cell: AreaLevelCellInput,
  counts: Record<string, number>,
): number {
  let num = 0;
  let den = 0;
  for (const d of cell.domains) {
    const n = counts[d.areaId];
    if (n === undefined) continue;
    num += n * d.directEstimate;
    den += n;
  }
  return den > 0 ? num / den : 0;
}
